import React, { createContext, FunctionComponent, PropsWithChildren, Suspense, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { graphql, useFragment, useLazyLoadQuery, useMutation } from 'react-relay';
import type { Result } from 'relay-runtime';
import { CustomErrorBoundary } from '../components/widgets/customErrorBoundary';
import type { backofficeUiPreferencesFragment$key } from './__generated__/backofficeUiPreferencesFragment.graphql';
import type { backofficeUiPreferencesReadQuery } from './__generated__/backofficeUiPreferencesReadQuery.graphql';
import type { backofficeUiPreferencesSaveMutation } from './__generated__/backofficeUiPreferencesSaveMutation.graphql';

// Come si presenta lo strumento a chi ci lavora — tema, attracchi, larghezze — per superficie
// («scope»: scene-editor, composer, …). Non è il tema del customizer né un dato di prodotto.
// Contratto e ragioni: docs/preferenze-interfaccia-microservizio.md.
//
// IL SERVER TIENE UN DOCUMENTO SOLO E LO SOVRASCRIVE INTERO — `{ scope: { chiave: valore } }`, una
// riga per seller e utente IAM. Da qui le regole di questo file:
//   1. il documento lo tiene uno solo, qui, e lo tiene intero: anche gli scope e le chiavi che non
//      conosce. Una superficie che salvasse solo il suo pezzo cancellerebbe le altre;
//   2. PRIMA DI SCRIVERE SI RILEGGE, e si scrive l'ultima lettura più i cambi fatti qui. Un'altra
//      scheda, o un altro computer, può aver salvato dopo di noi: mandare la nostra fotografia
//      riporterebbe indietro le sue scelte;
//   3. se la lettura fallisce non si scrive niente, perché si scriverebbe sopra un documento che
//      non si è visto.
//
// LA RILETTURA NON È RIDONDANTE, anche se all'avvio il documento l'abbiamo già. Quando è il servizio
// intero a cadere, il router risponde a una query con più servizi — com'è quella d'avvio — con il
// campo a `null` e un errore senza percorso: Relay non lo lega al campo, `@catch` non lo vede, e
// «servizio giù» diventa indistinguibile da «nessuna riga». La lettura prima di scrivere chiede solo
// questo campo: lì lo stesso guasto azzera tutta la risposta, e la rete lo tratta da errore.

export type UiPreferenceValues = Readonly<Record<string, string>>;
// `null` toglie la voce: una voce assente vuol dire «vale il default della superficie».
export type UiPreferenceChanges = Readonly<Record<string, string | null>>;

// `set` è la scelta di chi lavora. `fill` riempie solo le voci che mancano e non corregge niente:
// è per portare su valori vecchi senza coprire una scelta fatta altrove.
type WriteMode = 'set' | 'fill';
type UiPreferencesDocument = Readonly<Record<string, unknown>>;
type Patch = { scope: string; changes: UiPreferenceChanges; mode: WriteMode };
type ReadResult = { ok: true; document: UiPreferencesDocument } | { ok: false };
// Un errore del resolver arriva come `ok: false`, non come un `null` indistinguibile da «nessuna riga».
type CaughtPreference = Result<{ readonly data: string } | null, unknown>;

const fragment = graphql`
    fragment backofficeUiPreferencesFragment on Query {
        backofficeUiPreference @catch(to: RESULT) {
            data
        }
    }
`;

const readQuery = graphql`
    query backofficeUiPreferencesReadQuery {
        backofficeUiPreference @catch(to: RESULT) {
            data
        }
    }
`;

// La risposta è il documento appena mandato: non c'è niente da rileggere.
const saveMutation = graphql`
    mutation backofficeUiPreferencesSaveMutation($data: String!) {
        createOrUpdateBackofficeUiPreference(data: $data) {
            __typename
        }
    }
`;

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);

// Il backend non valida niente: il documento l'ha scritto chiunque. Si legge solo ciò che ha la
// forma attesa, ma si conserva tutto — alla prossima scrittura torna su com'era.
const parseDocument = (data: string | null | undefined): UiPreferencesDocument => {
    if (!data) return {};
    try {
        const parsed: unknown = JSON.parse(data);
        return isRecord(parsed) ? parsed : {};
    } catch {
        return {};
    }
};

const toReadResult = (field: CaughtPreference): ReadResult => (field.ok ? { ok: true, document: parseDocument(field.value?.data) } : { ok: false });

const applyChanges = (document: UiPreferencesDocument, { scope, changes, mode }: Patch): UiPreferencesDocument => {
    const stored = document[scope];
    const entries: Record<string, unknown> = isRecord(stored) ? { ...stored } : {};
    let changed = false;

    for (const [key, value] of Object.entries(changes)) {
        if (mode === 'fill' && key in entries) continue;
        if (value === null ? !(key in entries) : entries[key] === value) continue;
        if (value === null) delete entries[key];
        else entries[key] = value;
        changed = true;
    }

    // Lo stesso riferimento vuol dire «niente da salvare».
    if (!changed) return document;

    const next: Record<string, unknown> = { ...document };
    if (Object.keys(entries).length > 0) next[scope] = entries;
    else delete next[scope];
    return next;
};

const readScope = (stored: unknown): UiPreferenceValues => {
    if (!isRecord(stored)) return {};
    return Object.fromEntries(Object.entries(stored).filter((entry): entry is [string, string] => typeof entry[1] === 'string'));
};

interface BackofficeUiPreferencesContextValue {
    // Ciò che si vede: l'ultima lettura più i cambi fatti qui, anche quelli non ancora scritti.
    current: UiPreferencesDocument;
    // Ciò che il server ha di sicuro: l'ultima lettura o l'ultima scrittura riuscita.
    confirmed: UiPreferencesDocument;
    write: (patch: Patch) => void;
}

const BackofficeUiPreferencesContext = createContext<BackofficeUiPreferencesContextValue>({ current: {}, confirmed: {}, write: () => {} });

export const useBackofficeUiPreferences = (scope: string) => {
    const { current, confirmed, write } = useContext(BackofficeUiPreferencesContext);
    const stored = current[scope];
    const values = useMemo(() => readScope(stored), [stored]);
    const save = useCallback((changes: UiPreferenceChanges) => write({ scope, changes, mode: 'set' }), [scope, write]);
    const fill = useCallback((changes: UiPreferenceChanges) => write({ scope, changes, mode: 'fill' }), [scope, write]);
    // Il server ha questa superficie: ciò che il browser teneva da parte per lei non serve più.
    const isStored = confirmed[scope] !== undefined;

    return useMemo(() => ({ values, isStored, save, fill }), [fill, isStored, save, values]);
};

// Una lettura, una risposta: il componente nasce per ogni lettura, con la sua chiave.
const PreferencesReader: FunctionComponent<{ fetchKey: number; onRead: (result: ReadResult) => void }> = ({ fetchKey, onRead }) => {
    const { backofficeUiPreference } = useLazyLoadQuery<backofficeUiPreferencesReadQuery>(readQuery, {}, { fetchPolicy: 'network-only', fetchKey });
    const result = toReadResult(backofficeUiPreference);

    useEffect(() => {
        onRead(result);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return null;
};

// La rete non ha risposto, o la risposta era vuota: per chi aspetta è una lettura fallita come le altre.
const ReadFailure: FunctionComponent<{ onRead: (result: ReadResult) => void }> = ({ onRead }) => {
    useEffect(() => {
        onRead({ ok: false });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return null;
};

export const BackofficeUiPreferencesProvider: FunctionComponent<PropsWithChildren<{ fragmentRef: backofficeUiPreferencesFragment$key | null }>> = ({
    fragmentRef,
    children
}) => {
    const bootstrap = useFragment(fragment, fragmentRef);
    // La lettura d'avvio conta una volta sola: da lì il documento lo tiene questo stato. Il record
    // di Relay sotto il fragment lo riscrivono anche le letture che precedono ogni salvataggio.
    const [initial] = useState<UiPreferencesDocument>(() => {
        const read: ReadResult = bootstrap ? toReadResult(bootstrap.backofficeUiPreference) : { ok: false };
        return read.ok ? read.document : {};
    });
    // Senza fragment la lettura d'avvio è stata saltata (amministratori di piattaforma): non c'è un
    // seller, quindi nemmeno un posto dove scrivere. Le scelte valgono finché la pagina resta aperta.
    const canSave = fragmentRef !== null;

    const [current, setCurrent] = useState(initial);
    const [confirmed, setConfirmed] = useState(initial);
    const currentRef = useRef(current);
    const queue = useRef<Patch[]>([]);
    // Un giro alla volta — rileggi, poi scrivi. Chi cambia qualcosa mentre un giro è in corso
    // aspetta il successivo, che ricomincia rileggendo.
    const cycle = useRef<{ key: number; phase: 'reading' | 'writing' } | null>(null);
    const lastKey = useRef(0);
    const [readKey, setReadKey] = useState(0);
    const [commitSave] = useMutation<backofficeUiPreferencesSaveMutation>(saveMutation);

    const show = useCallback((next: UiPreferencesDocument) => {
        currentRef.current = next;
        setCurrent(next);
    }, []);

    const startCycle = useCallback(() => {
        if (!canSave || cycle.current || queue.current.length === 0) return;
        lastKey.current += 1;
        cycle.current = { key: lastKey.current, phase: 'reading' };
        setReadKey(lastKey.current);
    }, [canSave]);

    const handleRead = useCallback(
        (key: number, result: ReadResult) => {
            // Una risposta che non è quella attesa — un effetto ripetuto, un giro già chiuso — non conta.
            if (cycle.current?.key !== key || cycle.current.phase !== 'reading') return;

            // I cambi restano in coda, e il prossimo cambio riprova il giro intero.
            if (!result.ok) {
                cycle.current = null;
                return;
            }

            setConfirmed(result.document);
            const patches = queue.current.splice(0);
            const next = patches.reduce(applyChanges, result.document);
            show(next);
            cycle.current = { key, phase: 'writing' };
            commitSave({
                variables: { data: JSON.stringify(next) },
                onCompleted: () => {
                    setConfirmed(next);
                    cycle.current = null;
                    startCycle();
                },
                onError: () => {
                    queue.current.unshift(...patches);
                    cycle.current = null;
                }
            });
        },
        [commitSave, show, startCycle]
    );

    const write = useCallback(
        (patch: Patch) => {
            const next = applyChanges(currentRef.current, patch);
            if (next === currentRef.current) return;
            show(next);
            if (!canSave) return;
            queue.current.push(patch);
            startCycle();
        },
        [canSave, show, startCycle]
    );

    const value = useMemo(() => ({ current, confirmed, write }), [confirmed, current, write]);

    return (
        <BackofficeUiPreferencesContext.Provider value={value}>
            {readKey > 0 && (
                <CustomErrorBoundary key={readKey} render={() => <ReadFailure onRead={(result) => handleRead(readKey, result)} />}>
                    <Suspense fallback={null}>
                        <PreferencesReader fetchKey={readKey} onRead={(result) => handleRead(readKey, result)} />
                    </Suspense>
                </CustomErrorBoundary>
            )}
            {children}
        </BackofficeUiPreferencesContext.Provider>
    );
};
